import { useMemo, useState } from "react";
import {
  ECONOMICS,
  LOCK_OPTIONS,
  MIN_SUPPLY_AXM,
  buildSupplyTokenMsgs,
  estimateTokenAmountForMinAxm,
  formatCompact,
  type CosmosMsg,
  type EnrichedMarket,
} from "@axiome/counter-lp-sdk";
import { VAULT_ADDRESS } from "../lib/config";
import { friendlyError } from "../lib/friendlyError";

export function SupplyTokenPanel(props: {
  markets: EnrichedMarket[];
  account: string | null;
  liveEnabled?: boolean;
  minSupplyAxm?: number;
  supplierOfPlatform?: number;
  userPoolShare?: number;
  userLpBonusShare?: number;
  onConnect: () => void;
  onSign: (msgs: CosmosMsg[]) => Promise<void>;
}) {
  const [poolId, setPoolId] = useState("");
  const [amount, setAmount] = useState("");
  const [lockSeconds, setLockSeconds] = useState<number>(LOCK_OPTIONS[1].seconds);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  const minSupply = props.minSupplyAxm ?? MIN_SUPPLY_AXM;
  const supplierShare = props.supplierOfPlatform ?? ECONOMICS.supplierOfPlatform;
  const userPool = props.userPoolShare ?? ECONOMICS.userPoolShare;
  const userBonus = props.userLpBonusShare ?? ECONOMICS.userLpBonusShare;
  const platformPool = 1 - userPool;
  const platformBonus = 1 - userBonus;

  const market = useMemo(
    () => props.markets.find((m) => m.id === poolId) ?? null,
    [poolId, props.markets],
  );

  const valueAxm = useMemo(() => {
    if (!market || !amount) return 0;
    const n = Number(amount) || 0;
    return n * (market.priceTokenInAxm || 0);
  }, [amount, market]);

  const minTokens = market
    ? estimateTokenAmountForMinAxm(minSupply, market.priceTokenInAxm)
    : 0;
  const meetsMin = valueAxm >= minSupply;
  const priceReady = !!market && market.priceTokenInAxm > 0;

  async function submit() {
    if (!props.account) {
      props.onConnect();
      return;
    }
    if (props.liveEnabled === false) {
      setStatus("Сеть обновляется — попробуйте через минуту");
      return;
    }
    if (!VAULT_ADDRESS) {
      setStatus("Сеть обновляется — попробуйте через минуту");
      return;
    }
    if (!market) {
      setStatus("Выберите токен из списка");
      return;
    }
    if (!priceReady) {
      setStatus("Подождите оценку по рынку — цена ещё загружается");
      return;
    }
    if (!meetsMin) {
      setStatus(`Минимум — около ${formatCompact(minSupply)} AXM в эквиваленте`);
      return;
    }
    setBusy(true);
    setStatus(null);
    try {
      const base = {
        sender: props.account,
        tokenContract: market.token,
        vault: VAULT_ADDRESS,
        symbol: market.symbol,
        amountHuman: amount,
        decimals: market.tokenDecimals,
        pool: market.pool,
        lockSeconds,
      };
      try {
        await props.onSign(buildSupplyTokenMsgs(base));
      } catch (e) {
        const raw = e instanceof Error ? e.message : String(e);
        if (/HarvestRequired|harvest required/i.test(raw)) {
          await props.onSign(buildSupplyTokenMsgs({ ...base, withHarvest: true }));
        } else {
          throw e;
        }
      }
      setStatus("Откройте Axiome Wallet и подтвердите отправку");
    } catch (e) {
      setStatus(friendlyError(e, "Не удалось отправить — попробуйте ещё раз"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="section">
      <h2>Добавить свой токен</h2>
      <p className="lead">
        Отправьте токен в смартконтракт на выбранный срок. Пользователи будут вносить AXM —
        ваш токен пойдёт им в пару. Вы получаете {(supplierShare * 100).toFixed(0)}% от дохода
        платформы.
      </p>

      <div className="provide-box" style={{ marginTop: 0 }}>
        <div className="asset-split">
          <div>
            <div className="lbl">Ваша доля</div>
            <div className="nums">{(supplierShare * 100).toFixed(0)}% от платформы</div>
            <div className="hint" style={{ marginTop: 4 }}>
              ≈ {(platformPool * supplierShare * 100).toFixed(0)}% комиссий пула +{" "}
              {(platformBonus * supplierShare * 100).toFixed(0)}% LP Bonus
            </div>
          </div>
          <div>
            <div className="lbl">Минимум</div>
            <div className="nums">{formatCompact(minSupply)} AXM</div>
            <div className="hint" style={{ marginTop: 4 }}>
              в эквиваленте по цене пула
            </div>
          </div>
        </div>

        <div className="field">
          <label>Токен / пара</label>
          <select value={poolId} onChange={(e) => setPoolId(e.target.value)}>
            <option value="">— выберите из списка —</option>
            {props.markets.map((m) => (
              <option key={m.id} value={m.id}>
                {m.symbol}/AXM · {formatCompact(m.priceTokenInAxm, 4)} AXM
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label>Срок гарантии (лок)</label>
          <select
            value={lockSeconds}
            onChange={(e) => setLockSeconds(Number(e.target.value))}
          >
            {LOCK_OPTIONS.map((o) => (
              <option key={o.seconds} value={o.seconds}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label>Сумма токена {market ? `(${market.symbol})` : ""}</label>
          <input
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ""))}
            placeholder={minTokens ? `от ~${formatCompact(minTokens)}` : "0"}
          />
        </div>

        {market ? (
          <p
            className="hint"
            style={{ marginTop: 0, color: meetsMin && priceReady ? "var(--muted)" : "var(--danger)" }}
          >
            {!priceReady
              ? "Загружаем оценку по рынку…"
              : `Оценка: ${formatCompact(valueAxm)} AXM${
                  meetsMin
                    ? " · минимум выполнен"
                    : ` · нужно ещё ~${formatCompact(Math.max(0, minSupply - valueAxm))} AXM`
                }`}
          </p>
        ) : null}

        <p className="hint">
          После окончания лока можно вывести только свободный остаток. Токены, уже занятые в
          позициях пользователей, вывести нельзя, пока люди их держат.
        </p>

        {!props.account ? (
          <div className="cta-row">
            <button className="btn btn-primary" onClick={props.onConnect}>
              Подключить кошелёк
            </button>
          </div>
        ) : (
          <div className="cta-row">
            <button
              className="btn btn-primary"
              disabled={busy || !market || !amount || !meetsMin || !priceReady}
              onClick={submit}
            >
              {busy ? "Подтвердите в кошельке…" : "Отправить токены в контракт"}
            </button>
          </div>
        )}

        {status ? <p className="status">{status}</p> : null}
      </div>

      <div className="admin-block" style={{ marginTop: 16 }}>
        <h3>Как устроен доход</h3>
        <p className="hint" style={{ margin: 0 }}>
          Пользователи: {(userPool * 100).toFixed(0)}% комиссий пула и {(userBonus * 100).toFixed(0)}%
          LP Bonus. Платформа: остаток. Вы как поставщик токена получаете{" "}
          {(supplierShare * 100).toFixed(0)}% от доли платформы.
        </p>
      </div>
    </div>
  );
}
